import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardCheck, Loader2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScreeningOverview } from './screening/ScreeningOverview';
import { ScreeningAnswers } from './screening/ScreeningAnswers';
import { formatScreeningDate } from './screening/screeningDisplay';

export const ScreeningHistoryDialog = ({ isOpen, onClose, application, getAuthToken }) => {
  const [screenings, setScreenings] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!isOpen || !application?.id) return;
    const controller = new AbortController();
    setLoading(true); setError(''); setScreenings([]); setSelectedId('');
    const loadHistory = async () => {
      const records = new Map();
      let skip = 0;
      while (!controller.signal.aborted) {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/telephonic-screenings`, {
          params: { application_id: application.id, skip, limit: 200 }, signal: controller.signal,
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        if (!Array.isArray(response.data)) throw new Error('Unexpected screening response');
        const previousCount = records.size;
        response.data.forEach(record => records.set(record.id, record));
        if (response.data.length < 200 || records.size === previousCount) break;
        skip += response.data.length;
      }
      if (controller.signal.aborted) return;
      const history = [...records.values()];
      setScreenings(history); setSelectedId(history[0]?.id || '');
    };
    loadHistory().catch(err => {
      if (controller.signal.aborted || axios.isCancel(err)) return;
      setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not load saved screenings. Please try again.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [isOpen, application?.id, reload]);

  const selected = screenings.find(screening => screening.id === selectedId);
  return <Dialog open={isOpen} onOpenChange={open => { if (!open) onClose(); }}>
    <DialogContent data-testid="screening-history-dialog" className="w-[calc(100%-2rem)] max-w-5xl h-[90dvh] max-h-[90dvh] p-0 flex flex-col gap-0 overflow-hidden">
      <DialogHeader className="shrink-0 px-5 sm:px-7 py-5 pr-12 border-b border-slate-200">
        <DialogTitle className="flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-violet-600" />Screening details</DialogTitle>
        <DialogDescription>Saved screening history · read-only</DialogDescription>
      </DialogHeader>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 sm:px-7 py-5 space-y-6" data-testid="screening-history-content">
        {loading ? <div role="status" data-testid="screening-history-loading" className="flex items-center justify-center gap-2 py-12 text-sm text-slate-500"><Loader2 className="h-5 w-5 animate-spin" />Loading screenings…</div> : error ?
          <div className="space-y-3 py-6"><p role="alert" data-testid="screening-history-error" className="text-sm text-red-700 [overflow-wrap:anywhere]">{error}</p><Button data-testid="screening-history-retry" variant="outline" onClick={() => setReload(value => value + 1)}>Try again</Button></div> : !screenings.length ?
          <div data-testid="screening-history-empty" className="text-center py-12 space-y-3"><ClipboardCheck className="h-10 w-10 text-slate-300 mx-auto" /><p className="font-medium text-slate-700">No screenings recorded</p><p className="text-sm text-slate-500 [overflow-wrap:anywhere]">{application?.full_name || application?.applicant_name || application?.candidate_name || 'This application'}</p></div> : <>
            <div className="space-y-2"><Label htmlFor="screening-history-session">Screening session</Label>
              <Select value={selectedId} onValueChange={setSelectedId}><SelectTrigger id="screening-history-session" data-testid="screening-history-session-select" className="[&>span]:truncate"><SelectValue /></SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-3rem)]" data-testid="screening-history-session-options">
                  {screenings.map((screening, index) => <SelectItem key={screening.id} value={screening.id} data-testid={`screening-history-session-${screening.id}`} className="whitespace-normal [overflow-wrap:anywhere] [&>span:last-child]:min-w-0">
                    {index === 0 ? 'Latest · ' : ''}{formatScreeningDate(screening.started_at || screening.created_at)} · {screening.status || 'Unknown'} · {screening.screened_by_name || screening.screened_by || 'Recruiter not recorded'}
                  </SelectItem>)}
                </SelectContent>
              </Select>
              <p data-testid="screening-history-session-count" className="text-xs text-slate-500">{screenings.length} saved screening{screenings.length === 1 ? '' : 's'}</p>
            </div>
            {selected && <><ScreeningOverview screening={selected} application={application || {}} /><ScreeningAnswers key={selected.id} screening={selected} /></>}
          </>}
      </div>
      <footer className="shrink-0 flex flex-wrap justify-end gap-2 px-5 sm:px-7 py-3 border-t border-slate-200 bg-slate-50">
        <Button data-testid="screening-history-refresh" variant="outline" disabled={loading} onClick={() => setReload(value => value + 1)}><RefreshCw className="mr-2 h-4 w-4" />Refresh</Button>
        <Button data-testid="screening-history-close" onClick={onClose}>Close</Button>
      </footer>
    </DialogContent>
  </Dialog>;
};